import React from 'react';
import { i18n } from 'config/locales';
import { numbersOnlyPattern } from 'constants/patterns';
import ValidationElement from '../ValidationElement';
import Text from '../Text';
import Checkbox from '../Checkbox';
import Radio from '../Radio';
import RadioGroup from '../RadioGroup';
import Show from '../Show';
import { setAttribute } from '../shared/setAttribute';

const defaultNumbers = {
  domestic: {
    first: '',
    second: '',
    third: '',
  },
  DSN: {
    first: '',
    second: '',
    third: '',
  },
  international: {
    first: '',
  },
};

const padleft = (str, len, char = ' ') => {
  let padding = '';
  for (let i = 0; i < len; i += 1) {
    padding += char;
  }
  return padding.substring(0, padding.length - str.length) + str;
};

const trimleading = (str) => (str || '').trim();

const digitsOnly = (value = '') => {
  if (!(/^(\s*|\d+)$/.exec(value))) {
    return value.replace(/\D/g, '');
  }

  return value;
};

export default class Telephone extends ValidationElement {
  constructor(props) {
    super(props);

    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      domestic: {
        first: this.parseNumber(0, 3, props.number),
        second: this.parseNumber(3, 6, props.number),
        third: this.parseNumber(6, 10, props.number),
      },
      DSN: {
        first: this.parseNumber(0, 3, props.number),
        second: this.parseNumber(3, 6, props.number),
        third: this.parseNumber(6, 10, props.number),
      },
      international: {
        first: props.number,
      },
    };
    this.domestic = this.domestic.bind(this);
    this.DSN = this.DSN.bind(this);
    this.international = this.international.bind(this);
    this.errors = [];

    this.domesticFirst = React.createRef();
    this.domesticSecond = React.createRef();
    this.domesticThird = React.createRef();
    this.domesticExtension = React.createRef();
    this.dsnFirst = React.createRef();
    this.dsnSecond = React.createRef();
    this.dsnThird = React.createRef();
    this.dsnExtension = React.createRef();
    this.intFirst = React.createRef();
    this.intExtension = React.createRef();
    this.btnDomestic = React.createRef();
    this.btnInternational = React.createRef();
    this.btnDSN = React.createRef();
  }

  parseNumber = (start, end, number) => {
    if (!number) {
      return '';
    }
    return number.substring(start, end);
  };

  update = (queue) => {
    this.props.onUpdate({
      name: this.props.name,
      timeOfDay: this.props.showTimeOfDay ? this.props.timeOfDay : 'NA',
      type: this.props.type || 'Domestic',
      numberType: this.props.numberType,
      number: this.getFormattedNumber(),
      extension: this.props.extension,
      noNumber: this.props.noNumber || false,
      showTimeOfDay: this.props.showTimeOfDay,
      ...queue,
    });
  };

  updateNumber = (type) => {
    this.update({
      type,
      noNumber: false,
      extension: '',
    });
  };

  updateToggleDomestic = () => {
    this.setState(
      {
        ...defaultNumbers,
      },
      () => {
        this.updateNumber('Domestic');
        this.btnInternational.current
          ? this.btnInternational.current.focus()
          : this.btnDSN.current.focus();
      }
    );
  };

  updateToggleInternational = () => {
    this.setState(
      {
        ...defaultNumbers,
      },
      () => {
        this.updateNumber('International');
        this.btnDSN.current ? this.btnDSN.current.focus() : this.btnDomestic.current.focus();
      }
    );
  };

  updateToggleDSN = () => {
    this.setState(
      {
        ...defaultNumbers,
      },
      () => {
        this.updateNumber('DSN');
        this.btnDomestic.current // eslint-disable-line no-unused-expressions
          ? this.btnDomestic.current.focus()
          : this.btnInternational.current.focus();
      }
    );
  };

  updateDomesticNumber = (values) => {
    const fieldNameMap = {
      domesticFirstField: 'first',
      domesticSecondField: 'second',
      domesticThirdField: 'third',
    };

    this.setState(
      (prevState) => ({
        domestic: {
          ...prevState.domestic,
          [fieldNameMap[values.name]]: values.value,
        },
      }),
      () => {
        this.updateNumber('Domestic');
      }
    );
  };

  updateDSNNumber = (values) => {
    const fieldNameMap = {
      dsnFirstField: 'first',
      dsnSecondField: 'second',
      dsnThirdField: 'third',
    };

    this.setState(
      (prevState) => ({
        DSN: {
          ...prevState.DSN,
          [fieldNameMap[values.name]]: values.value,
        },
      }),
      () => {
        this.updateNumber('DSN');
      }
    );
  };

  updateInternationalNumber = (values) => {
    const fieldNameMap = {
      intFirstField: 'first',
    };

    this.setState(
      (prevState) => ({
        international: {
          ...prevState.international,
          [fieldNameMap[values.name]]: values.value,
        },
      }),
      () => {
        this.updateNumber('International');
      }
    );
  };

  updateExtension = (values) => {
    this.update({
      extension: values.value,
    });
  };

  updateNoNumber = () => {
    this.setState(
      {
        ...defaultNumbers,
      },
      () => {
        const newNoNumber = !this.props.noNumber;
        this.update({
          noNumber: newNoNumber,
          timeOfDay: newNoNumber ? '' : this.props.timeOfDay,
          numberType: newNoNumber ? '' : this.props.numberType,
          number: newNoNumber ? '' : this.props.number,
          extension: newNoNumber ? '' : this.props.extension,
        });
      }
    );
  };

  updateTimeOfDay = (values) => {
    this.update({
      timeOfDay: values.value,
    });
  };

  updateNumberType = (values) => {
    this.update({
      numberType: values.value,
    });
  };

  getFormattedNumber = () => {
    switch (this.props.type) {
      case 'Domestic':
        return [
          padleft(this.state.domestic.first, 3),
          padleft(this.state.domestic.second, 3),
          padleft(this.state.domestic.third, 4),
        ]
          .join('')
          .trim();
      case 'DSN':
        return [
          padleft(this.state.DSN.first, 3),
          padleft(this.state.DSN.second, 3),
          padleft(this.state.DSN.third, 4),
        ]
          .join('')
          .trim();
      case 'International':
        return this.state.international.first.trim();
      default:
        return '';
    }
  };

  handleErrorDomesticFirst = (value, arr) => this.handleErrorDomestic('first', value, arr);

  handleErrorDomesticSecond = (value, arr) => this.handleErrorDomestic('second', value, arr);

  handleErrorDomesticThird = (value, arr) => this.handleErrorDomestic('third', value, arr);

  handleErrorDomesticExtension = (value, arr) => this.handleErrorDomestic('extension', value, arr);

  handleErrorDomestic = (code, value, arr) => this.handleError(`domestic.${code}`, value, arr);

  handleErrorInternationalFirst = (value, arr) => this.handleErrorInternational('first', value, arr);

  handleErrorInternationalExtension = (value, arr) => this.handleErrorInternational('extension', value, arr);

  handleErrorInternational = (code, value, arr) => this.handleError(`international.${code}`, value, arr);

  handleErrorNoNumber = (value, arr) => this.handleError('none', value, arr);

  handleErrorTime = (value, arr) => this.handleError('time', value, arr);

  handleErrorType = (value, arr) => this.handleError('type', value, arr);

  handleErrorNumberType = (value, arr) => this.handleError('numberType', value, arr);

  handleError = (code, value, arr) => {
    const localErr = arr.map((err) => ({
      code: `telephone.${code}.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    // Replace errors with new values
    for (const err of localErr) {
      const idx = this.errors.findIndex((x) => x.code === err.code);
      if (idx > -1) {
        this.errors[idx] = err;
      } else {
        this.errors.push(err);
      }
    }

    // Nullify unused codes
    const allowedTypes = ['Domestic', 'International'];
    allowedTypes
      .filter((x) => x !== (this.props.type || 'Domestic'))
      .forEach((x) => {
        this.errors
          .filter((err) => err.code.indexOf(`telephone.${x.toLowerCase()}.`) > -1)
          .forEach((err) => {
            err.valid = null; // eslint-disable-line no-param-reassign
          });
      });

    // Zero out any required fields if IDK is selected
    if (code === 'none' && value === true) {
      this.errors
        .filter((err) => err.code.indexOf('.required') > -1)
        .forEach((err) => {
          err.valid = true; // eslint-disable-line no-param-reassign
        });
    }

    // Run the entire component through it's own error checks as a whole
    const requiredErr = this.errors.concat(
      this.constructor.errors.map((err) => {
        const errProps = {
          ...this.props,
          ...this.state,
        };
        if (code === 'none') {
          errProps.noNumber = value;
        }

        return {
          code: `telephone.${err.code}`,
          valid: err.func(value, errProps),
          uid: this.state.uid,
        };
      })
    );

    // Take the original and concatenate our new error values to it
    this.props.onError(value, requiredErr);
    return localErr;
  };

  getAriaNumberType = () => (this.props.numberType ? `${this.props.numberType} phone ` : '');

  domestic() {
    const ariaNumberType = this.getAriaNumberType();
    return (
      <div className="numbers">
        <label
          className={[`${this.props.typeClass || ''}`, `${this.props.noNumber ? 'disabled' : ''}`]
            .join(' ')
            .trim()}
        >
          {i18n.t('telephone.domestic.label')}
        </label>
        <div className="telephone-number-fields">
          <div className="telephone-number-fields-main">
            <span className="separator">(</span>
            <Text
              attribute={setAttribute(this.props.attribute, 'number')}
              name="domesticFirstField"
              ref={this.domesticFirst}
              className="number three"
              label=""
              ariaLabel={ariaNumberType + i18n.t('telephone.aria.domesticAreaCode')}
              disabled={this.props.noNumber}
              maxlength="3"
              pattern="^((?!(0))\d{3})"
              inputFilter={numbersOnlyPattern}
              prefilter={digitsOnly}
              readonly={this.props.readonly}
              required={this.required('Domestic')}
              value={trimleading(this.state.domestic.first)}
              onUpdate={this.updateDomesticNumber}
              onError={this.handleErrorDomesticFirst}
              tabNext={() => {
                this.props.tab(this.domesticSecond.current.text.current.input.current);
              }}
            />
            <span className="separator">)</span>
            <Text
              attribute={setAttribute(this.props.attribute, 'number')}
              name="domesticSecondField"
              ref={this.domesticSecond}
              className="number three"
              label=""
              ariaLabel={ariaNumberType + i18n.t('telephone.aria.domesticThree')}
              disabled={this.props.noNumber}
              maxlength="3"
              pattern="\d{3}"
              inputFilter={numbersOnlyPattern}
              prefilter={digitsOnly}
              readonly={this.props.readonly}
              required={this.required('Domestic')}
              value={trimleading(this.state.domestic.second)}
              onUpdate={this.updateDomesticNumber}
              onError={this.handleErrorDomesticSecond}
              tabBack={() => {
                this.props.tab(this.domesticFirst.current.text.current.input.current);
              }}
              tabNext={() => {
                this.props.tab(this.domesticThird.current.text.current.input.current);
              }}
            />
            <span className="separator">-</span>
            <Text
              attribute={setAttribute(this.props.attribute, 'number')}
              name="domesticThirdField"
              ref={this.domesticThird}
              className="number four"
              label=""
              ariaLabel={ariaNumberType + i18n.t('telephone.aria.domesticFour')}
              disabled={this.props.noNumber}
              minlengh="4"
              maxlength="4"
              pattern="\d{4}"
              inputFilter={numbersOnlyPattern}
              prefilter={digitsOnly}
              readonly={this.props.readonly}
              required={this.required('Domestic')}
              value={trimleading(this.state.domestic.third)}
              onUpdate={this.updateDomesticNumber}
              onError={this.handleErrorDomesticThird}
              tabBack={() => {
                this.props.tab(this.domesticSecond.current.text.current.input.current);
              }}
              tabNext={() => {
                this.props.tab(this.domesticExtension.current.text.current.input.current);
              }}
            />
          </div>
          <div className="telephone-number-fields-extension">
            <span className="separator pound">#</span>
            <Text
              attribute={setAttribute(this.props.attribute, 'extension')}
              name="domesticExtension"
              ref={this.domesticExtension}
              className="number six"
              label={i18n.t('telephone.domestic.extension.label')}
              ariaLabel={ariaNumberType + i18n.t('telephone.aria.extension')}
              disabled={this.props.noNumber}
              maxlength="10"
              pattern="^\d{0,10}$"
              inputFilter={numbersOnlyPattern}
              prefilter={digitsOnly}
              readonly={this.props.readonly}
              required={false}
              value={this.props.extension}
              onUpdate={this.updateExtension}
              onError={this.handleErrorDomesticExtension}
              tabBack={() => {
                this.props.tab(this.domesticThird.current.text.current.input.current);
              }}
            />
          </div>
          <div className="telephone-number-fields-na">
            <span className={this.props.allowNotApplicable ? 'separator extension' : 'hidden'}>
              or
            </span>
            <Checkbox
              name="nonumber"
              className={this.props.allowNotApplicable ? 'nonumber' : 'hidden'}
              label={i18n.t('telephone.noNumber.label')}
              value="NA"
              checked={this.props.noNumber}
              onUpdate={this.updateNoNumber}
              onError={this.handleErrorNoNumber}
            />
          </div>
        </div>
      </div>
    );
  }

  DSN() {
    const ariaNumberType = this.getAriaNumberType();

    return (
      <div className="numbers">
        <label
          className={[`${this.props.typeClass || ''}`, `${this.props.noNumber ? 'disabled' : ''}`]
            .join(' ')
            .trim()}
        >
          {i18n.t('telephone.DSN.label')}
        </label>
        <div className="telephone-number-fields">
          <div className="telephone-number-fields-main">
            <span className="separator">(</span>
            <Text
              attribute={setAttribute(this.props.attribute, 'number')}
              name="dsnFirstField"
              ref={this.dsnFirst}
              className="number three"
              label=""
              ariaLabel={ariaNumberType + i18n.t('telephone.aria.DSNAreaCode')}
              disabled={this.props.noNumber}
              maxlength="3"
              pattern="^((?!(0))\d{3})"
              inputFilter={numbersOnlyPattern}
              prefilter={digitsOnly}
              readonly={this.props.readonly}
              required={this.required('DSN')}
              value={trimleading(this.state.DSN.first)}
              onUpdate={this.updateDSNNumber}
              onError={this.handleErrorDSNFirst}
              tabNext={() => {
                this.props.tab(this.dsnSecond.current.text.current.input.current);
              }}
            />
            <span className="separator">)</span>
            <Text
              attribute={setAttribute(this.props.attribute, 'number')}
              name="dsnSecondField"
              ref={this.dsnSecond}
              className="number three"
              label=""
              ariaLabel={ariaNumberType + i18n.t('telephone.aria.DSNThree')}
              disabled={this.props.noNumber}
              maxlength="3"
              pattern="\d{3}"
              inputFilter={numbersOnlyPattern}
              prefilter={digitsOnly}
              readonly={this.props.readonly}
              required={this.required('DSN')}
              value={trimleading(this.state.DSN.second)}
              onUpdate={this.updateDSNNumber}
              onError={this.handleErrorDSNSecond}
              tabBack={() => {
                this.props.tab(this.dsnFirst.current.text.current.input.current);
              }}
              tabNext={() => {
                this.props.tab(this.dsnThird.current.text.current.input.current);
              }}
            />
            <span className="separator">-</span>
            <Text
              attribute={setAttribute(this.props.attribute, 'number')}
              name="dsnThirdField"
              ref={this.dsnThird}
              className="number four"
              label=""
              ariaLabel={ariaNumberType + i18n.t('telephone.aria.DSNFour')}
              disabled={this.props.noNumber}
              minlengh="4"
              maxlength="4"
              pattern="\d{4}"
              inputFilter={numbersOnlyPattern}
              prefilter={digitsOnly}
              readonly={this.props.readonly}
              required={this.required('DSN')}
              value={trimleading(this.state.DSN.third)}
              onUpdate={this.updateDSNNumber}
              onError={this.handleErrorDSNThird}
              tabBack={() => {
                this.props.tab(this.dsnSecond.current.text.current.input.current);
              }}
              tabNext={() => {
                this.props.tab(this.dsnExtension.current.text.current.input.current);
              }}
            />
          </div>
          <div className="telephone-number-fields-extension">
            <span className="separator pound">#</span>
            <Text
              attribute={setAttribute(this.props.attribute, 'extension')}
              name="dsnExtension"
              ref={this.dsnExtension}
              className="number six"
              label={i18n.t('telephone.DSN.extension.label')}
              ariaLabel={ariaNumberType + i18n.t('telephone.aria.extension')}
              disabled={this.props.noNumber}
              maxlength="10"
              pattern="^\d{0,10}$"
              inputFilter={numbersOnlyPattern}
              prefilter={digitsOnly}
              readonly={this.props.readonly}
              required={false}
              value={this.props.extension}
              onUpdate={this.updateExtension}
              onError={this.handleErrorDSNExtension}
              tabBack={() => {
                this.props.tab(this.dsnThird.current.text.current.input.current);
              }}
            />
          </div>
          <div className="telephone-number-fields-na">
            <span className={this.props.allowNotApplicable ? 'separator extension' : 'hidden'}>
              or
            </span>
            <Checkbox
              name="nonumber"
              className={this.props.allowNotApplicable ? 'nonumber' : 'hidden'}
              label={i18n.t('telephone.noNumber.label')}
              value="NA"
              checked={this.props.noNumber}
              onUpdate={this.updateNoNumber}
              onError={this.handleErrorNoNumber}
            />
          </div>
        </div>
      </div>
    );
  }

  international() {
    const ariaNumberType = this.getAriaNumberType();
    return (
      <div className="international numbers">
        <label
          className={[`${this.props.typeClass || ''}`, `${this.props.noNumber ? 'disabled' : ''}`]
            .join(' ')
            .trim()}
        >
          {i18n.t('telephone.international.label')}
        </label>
        <div className="telephone-number-fields-main">
          <span className="separator">+</span>
          <Text
            attribute={setAttribute(this.props.attribute, 'number')}
            name="intFirstField"
            ref={this.intFirst}
            className="number ten"
            label=""
            ariaLabel={ariaNumberType + i18n.t('telephone.aria.phoneNumber')}
            disabled={this.props.noNumber}
            maxlength="20"
            pattern="\d{4,20}"
            inputFilter={numbersOnlyPattern}
            prefilter={digitsOnly}
            readonly={this.props.readonly}
            required={this.required('International')}
            value={trimleading(this.state.international.first)}
            onUpdate={this.updateInternationalNumber}
            onError={this.handleErrorInternationalFirst}
            tabNext={() => {
              this.props.tab(this.intExtension.current.text.current.input.current);
            }}
          />
        </div>
        <div className="telephone-number-fields-extension">
          <span className="separator pound">#</span>
          <Text
            attribute={setAttribute(this.props.attribute, 'int_extension')}
            name="int_extension"
            ref={this.intExtension}
            className="number six"
            label={i18n.t('telephone.international.extension.label')}
            ariaLabel={ariaNumberType + i18n.t('telephone.aria.extension')}
            disabled={this.props.noNumber}
            maxlength="10"
            pattern="^\d{0,10}$"
            inputFilter={numbersOnlyPattern}
            prefilter={digitsOnly}
            readonly={this.props.readonly}
            required={false}
            value={this.props.extension}
            onUpdate={this.updateExtension}
            onError={this.handleErrorInternationalExtension}
            tabBack={() => {
              this.props.tab(this.intFirst.current.text.current.input.current);
            }}
          />
        </div>
        <div className="telephone-number-fields-na">
          <span className={this.props.allowNotApplicable ? 'separator extension' : 'hidden'}>
            or
          </span>
          <Checkbox
            attribute={setAttribute(this.props.attribute, 'allowNotApplicable')}
            name="nonumber"
            className={this.props.allowNotApplicable ? 'nonumber' : 'hidden'}
            label={i18n.t('telephone.noNumber.label')}
            value="NA"
            checked={this.props.noNumber}
            onUpdate={this.updateNoNumber}
            onError={this.handleErrorNoNumber}
          />
        </div>
      </div>
    );
  }

  required = (type) => {
    if (type && type !== (this.props.type || 'Domestic')) {
      return false;
    }

    if (this.props.allowNotApplicable && this.props.noNumber) {
      return false;
    }

    return this.props.required;
  };

  render() {
    const klass = `telephone ${this.props.className || ''}`.trim();
    const phoneType = this.props.type || 'Domestic';
    const ariaNumberType = this.getAriaNumberType();
    return (
      <div className={klass}>
        <Show when={this.props.label}>
          <span>{this.props.label}</span>
        </Show>
        <div className="type screen-only">
          Switch to:
          <Show when={phoneType !== 'Domestic'}>
            <span className="type">
              <button
                type="button"
                className="domestic-number link"
                onClick={this.updateToggleDomestic}
                title={i18n.t('telephone.aria.domestic')}
                aria-label={i18n.t('telephone.aria.domestic')}
                ref={this.btnDomestic}
              >
                {i18n.t('telephone.type.domestic')}
              </button>
            </span>
          </Show>
          <Show when={phoneType !== 'International'}>
            <span className="type">
              <button
                type="button"
                className="international-number link"
                onClick={this.updateToggleInternational}
                title={i18n.t('telephone.aria.international')}
                aria-label={i18n.t('telephone.aria.international')}
                ref={this.btnInternational}
              >
                {i18n.t('telephone.type.international')}
              </button>
            </span>
          </Show>
          <Show when={phoneType !== 'DSN'}>
            <span className="type">
              <button
                type="button"
                className="DSN-number link"
                onClick={this.updateToggleDSN}
                title={i18n.t('telephone.aria.DSN')}
                aria-label={i18n.t('telephone.aria.DSN')}
                ref={this.btnDSN}
              >
                {i18n.t('telephone.type.DSN')}
              </button>
            </span>
          </Show>
        </div>

        <Show when={phoneType === 'Domestic'}>{this.domestic()}</Show>

        <Show when={phoneType === 'International'}>{this.international()}</Show>

        <Show when={phoneType === 'DSN'}>{this.DSN()}</Show>

        <Show when={this.props.showTimeOfDay}>
          <div className="timeofday">
            {this.props.timeOfDayLabel !== '' && (
              <span>{this.props.timeOfDayLabel}</span>
            )}
            <RadioGroup
              attribute={setAttribute(this.props.attribute, 'timeOfDay')}
              selectedValue={this.props.timeOfDay}
              name="timeofday"
              disabled={this.props.noNumber}
            >
              <Radio
                native
                className="time day block"
                label={i18n.t('telephone.timeOfDay.day')}
                value="Day"
                ariaLabel={ariaNumberType + i18n.t('telephone.aria.day')}
                disabled={this.props.noNumber}
                onUpdate={this.updateTimeOfDay}
                onError={this.handleErrorTime}
              />
              <Radio
                native
                className="time night block"
                label={i18n.t('telephone.timeOfDay.night')}
                value="Night"
                ariaLabel={ariaNumberType + i18n.t('telephone.aria.night')}
                disabled={this.props.noNumber}
                onUpdate={this.updateTimeOfDay}
                onError={this.handleErrorTime}
              />
              <Radio
                native
                className="time both block"
                label={i18n.t('telephone.timeOfDay.both')}
                value="Both"
                ariaLabel={ariaNumberType + i18n.t('telephone.aria.both')}
                disabled={this.props.noNumber}
                onUpdate={this.updateTimeOfDay}
                onError={this.handleErrorTime}
              />
            </RadioGroup>
          </div>
        </Show>

        <Show when={this.props.showNumberType}>
          <div className={`phonetype ${this.props.noNumber ? 'disabled' : ''}`.trim()}>
            <label id="numbertype">{i18n.t('telephone.numberType.title')}</label>
            <RadioGroup
              selectedValue={this.props.numberType}
              required={this.required()}
              disabled={this.props.noNumber}
              attribute={setAttribute(this.props.attribute, 'numberType')}
            >
              <Radio
                name="numbertype-cell"
                className="phonetype-option cell"
                label={i18n.t('telephone.numberType.cell')}
                value="Cell"
                ariaLabel={i18n.t('telephone.aria.cell')}
                disabled={this.props.noNumber}
                onUpdate={this.updateNumberType}
                onError={this.handleErrorNumberType}
              />
              <Radio
                name="numbertype-home"
                className="phonetype-option home"
                label={i18n.t('telephone.numberType.home')}
                value="Home"
                ariaLabel={i18n.t('telephone.aria.home')}
                disabled={this.props.noNumber}
                onUpdate={this.updateNumberType}
                onError={this.handleErrorNumberType}
              />
              <Radio
                name="numbertype-work"
                className="phonetype-option work"
                label={i18n.t('telephone.numberType.work')}
                value="Work"
                ariaLabel={i18n.t('telephone.aria.work')}
                disabled={this.props.noNumber}
                onUpdate={this.updateNumberType}
                onError={this.handleErrorNumberType}
              />
            </RadioGroup>
          </div>
        </Show>
      </div>
    );
  }
}

Telephone.defaultProps = {
  name: 'telephone',
  value: '',
  type: 'Domestic',
  typeClass: '',
  numberType: '',
  timeOfDay: 'Both',
  number: '',
  extension: '',
  noNumber: false,
  showNumberType: false,
  showTimeOfDay: true,
  allowNotApplicable: true,
  formType: '',
  tab: (input) => {
    input.focus();
  },
  timeOfDayLabel: '',
  onUpdate: () => { },
  onError: (value, arr) => arr,
};

Telephone.errors = [
  {
    code: 'required',
    func: (value, props) => {
      if (props.required) {
        if (props.allowNotApplicable && props.noNumber) {
          return true;
        }

        if (props.showNumberType && !props.numberType) {
          return false;
        }

        if (props.showTimeOfDay && (!props.timeOfDay || props.timeOfDay === '')) {
          return false;
        }

        switch (props.type) {
          case 'Domestic':
            return !!props.domestic.first && !!props.domestic.second && !!props.domestic.third;
          case 'DSN':
            return !!props.DSN.first && !!props.DSN.second && !!props.DSN.third;
          case 'International':
            return !!props.international.first;
          default:
            return false;
        }
      }

      return true;
    },
  },
];
